import { queryOptions } from '@tanstack/react-query'
import type { components, operations } from '@/api/schema'
import type { QuoteBuilderApi } from '../api/client'
import type { QuoteLine } from '../api/types'

// The live catalog reads (USA-1018) the quote screens share, keyed as the demo keys them:
// ['catalog', 'products', search], ['catalog', 'product', id], ['catalog', 'configuration', id].

type Schemas = components['schemas']

export type ProductSummary = Schemas['QbProductSummary']
export type ProductDetail = Schemas['QbProductDetail']
export type ProductSearchPage = Schemas['QbProductSearchPage']
// Tracked for quoting but not on the storefront: Regency, Safeware plans (USA-1109).
export type QuoteOnlyProduct = Schemas['QbQuoteOnlyProduct']
export type ProductFacets = Schemas['QbProductFacets']
export type ProductConfiguration = Schemas['QbProductConfiguration']
export type ConfigurationGroup = Schemas['QbConfigurationGroup']
export type ConfigurationOption = Schemas['QbConfigurationOption']
export type CatalogAsset = Schemas['QbAssetRead']
export type AssetDownload = Schemas['QbAssetDownload']
export type EquipmentAlternatives = Schemas['QbEquipmentAlternatives']
export type ComparisonEquipment = Schemas['QbComparisonEquipment']
export type ManufacturerList = Schemas['QbManufacturerList']
export type ProductSearch = NonNullable<
  operations['products_dashboard_quote_builder_catalog_products_get']['parameters']['query']
>
export type ProductSort = NonNullable<ProductSearch['sort']>

/** Alternatives a line can show on draft publications. */
export const MAX_DRAFT_ALTERNATIVES = 3

/** The Equipment stage's first catalog page, warmed while the quote opens (demo `equipmentCatalogSearch`). */
export const equipmentCatalogSearch: ProductSearch = {
  sort: 'featured',
  page: 1,
  page_size: 20,
}

function searchParams(search: ProductSearch) {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null || value === '') continue
    for (const item of Array.isArray(value) ? value : [value]) params.append(key, String(item))
  }
  return params
}

export const productsQueryOptions = (api: QuoteBuilderApi, search: ProductSearch) =>
  queryOptions({
    queryKey: ['catalog', 'products', search],
    queryFn: ({ signal }) => api.request<ProductSearchPage>(`/catalog/products?${searchParams(search)}`, { signal }),
    placeholderData: (previous) => previous,
  })

export const manufacturersQueryOptions = (api: QuoteBuilderApi) =>
  queryOptions({
    queryKey: ['catalog', 'manufacturers'],
    queryFn: ({ signal }) => api.request<ManufacturerList>('/catalog/manufacturers', { signal }),
    staleTime: 30 * 60_000,
  })

export const productQueryOptions = (api: QuoteBuilderApi, productId: number) =>
  queryOptions({
    queryKey: ['catalog', 'product', productId],
    queryFn: ({ signal }) => api.request<ProductDetail>(`/catalog/products/${productId}`, { signal }),
  })

export const configurationQueryOptions = (api: QuoteBuilderApi, productId: number) =>
  queryOptions({
    queryKey: ['catalog', 'configuration', productId],
    queryFn: ({ signal }) =>
      api.request<ProductConfiguration>(`/catalog/products/${productId}/configuration`, { signal }),
  })

export const assetDownloadQueryOptions = (api: QuoteBuilderApi, assetId: number) =>
  queryOptions({
    queryKey: ['catalog', 'asset', assetId],
    queryFn: ({ signal }) =>
      api.request<AssetDownload>(`/catalog/assets/${assetId}/download`, {
        signal,
      }),
    staleTime: 4 * 60_000,
    retry: false,
  })

export function equipmentAlternativesQueryOptions(
  api: QuoteBuilderApi,
  quoteId: string,
  line: Pick<QuoteLine, 'id' | 'product_id' | 'configuration' | 'alternative_product_ids'>,
) {
  return queryOptions({
    // Replacement keeps the line id; only the equipment and its saved choices change this comparison.
    queryKey: [
      'equipment-alternatives',
      quoteId,
      line.id,
      line.product_id,
      Boolean(Object.keys(line.configuration).length),
      line.alternative_product_ids ?? [],
    ],
    queryFn: ({ signal }) =>
      api.request<EquipmentAlternatives>(`/quotes/${quoteId}/lines/${line.id}/alternatives`, { signal }),
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  })
}

/** Groups a line picks one option from; accessories are added as their own sub-lines. */
export function configurationChoiceGroups(configuration: ProductConfiguration | undefined) {
  return configuration?.groups.filter((group) => group.relationship_type === 'configuration') ?? []
}

export function accessoryGroups(configuration: ProductConfiguration | undefined) {
  return configuration?.groups.filter((group) => group.relationship_type === 'accessory') ?? []
}

/** Choices whose values are sibling SKUs (Gas type: Natural gas | Liquid propane); picking one
 * replaces the line's product rather than being saved as a choice (USA-1392). */
export function versionGroups(configuration: ProductConfiguration | undefined) {
  return configuration?.versions ?? []
}

export function configurationOptionName(option: ConfigurationOption) {
  return option.label?.trim() || option.product?.title || option.id
}

/**
 * Each group's chosen option: the saved choice, else the catalog default (demo rule). A choice saved
 * before the product's options were cleaned up (USA-1392) names an option that is no longer listed;
 * it carries over by its recorded label (`labels`, the line's `configuration_display`) when one
 * option still reads the same, otherwise the group starts from its default.
 */
export function defaultChoices(
  groups: ConfigurationGroup[],
  current: Record<string, unknown> = {},
  labels: Record<string, string> = {},
) {
  const choices: Record<string, string> = {}
  for (const group of groups) {
    const saved = current[group.name]
    const label = labels[group.name]?.trim().toLowerCase()
    const value =
      (saved != null ? group.options.find((option) => option.id === String(saved))?.id : undefined) ??
      (label ? group.options.find((option) => option.label?.trim().toLowerCase() === label)?.id : undefined) ??
      group.options.find((option) => option.is_default && option.available)?.id
    if (value) choices[group.name] = value
  }
  return choices
}
